import { useEffect, useMemo } from 'react';
import { useAuth } from '../features/auth/hooks/useAuth';
import { useModels } from '../shared/hooks/useModels';
import { useUser } from '../features/auth/hooks/useUser';
import { useChatStore } from '../features/chat/store/chatStore';
import { useBotStore } from '../shared/store/botStore';
import { useDocumentTitle } from '../shared/hooks/useDocumentTitle';
import { chatService } from '../shared/services/ChatService';
import { AssistantMapper } from '../shared/services/AssistantMapper';
import { ChatSessionService } from '../shared/services/ChatSessionService';
import { useAuthStore } from '../features/auth/store/authStore';
import { selectDefaultModel } from '../shared/utils/modelSelection';
import { useIsAuthRoute } from '../features/auth/hooks/useIsAuthRoute';
import { shallow } from 'zustand/shallow';
import { Model, Bot } from '../shared/types/api.types';
import { applyUserSettingsToBot } from '../shared/utils/userSettings';
import type { BotItem } from '../features/assistant-hub/types';

export function AppInitializer({ children }: { children: React.ReactNode }) {
  const { user: authUser } = useAuth(); // Initialize auth state and get user
  const isAuthRoute = useIsAuthRoute();

  // Only fetch models when not on auth pages
  const shouldFetchModels = !isAuthRoute;
  const { models = [] } = useModels({ enabled: shouldFetchModels });
  // Be defensive: ensure models is always an array
  const safeModels = Array.isArray(models) ? models : ([] as Model[]);

  // Only fetch user preferences when authenticated and not on auth pages
  const { preferences, defaultAssistant, user } = useUser({
    enabled: shouldFetchModels && !!authUser,
  });

  const effectiveDefaultAssistant =
    defaultAssistant ?? authUser?.defaultAssistant ?? null;

  // Note: Bot data is now fetched only when needed (in AssistantHub and CreateAssistant)
  const {
    currentModel,
    setCurrentModel,
    chatSource,
    currentBot,
    hasMultimodalFilesInConversation,
  } = useChatStore(
    state => ({
      currentModel: state.currentModel,
      setCurrentModel: state.setCurrentModel,
      chatSource: state.chatSource,
      currentBot: state.currentBot,
      hasMultimodalFilesInConversation: state.hasMultimodalFilesInConversation,
    }),
    shallow
  );

  const { setDefaultBot, setSelectedBot } = useBotStore(
    state => ({
      setDefaultBot: state.setDefaultBot,
      setSelectedBot: state.setSelectedBot,
    }),
    shallow
  );

  // Update document title based on current language
  useDocumentTitle();

  // Initialize auth from stored token on mount
  const { initializeFromStoredToken } = useAuthStore();

  useEffect(() => {
    // Try to restore authentication from stored token
    initializeFromStoredToken();
  }, [initializeFromStoredToken]);

  // Update chatService userId whenever user changes
  useEffect(() => {
    if (authUser?.email) {
      chatService.setUserId(authUser.email);
    } else {
      chatService.setUserId(undefined);
    }
  }, [authUser?.email]);

  useEffect(() => {
    let isCancelled = false;

    const initializeDefaultAssistant = async () => {
      const existingDefaultBot = useBotStore.getState().defaultBot;

      if (!effectiveDefaultAssistant) {
        if (existingDefaultBot) {
          setDefaultBot(null);
        }
        return;
      }

      const assistantId = String(effectiveDefaultAssistant.id);

      // If we already have the same default bot configured, re-apply user settings
      // (user data may have loaded after initial bot setup) and exit early
      if (existingDefaultBot && existingDefaultBot._id === assistantId) {
        const updatedBot = applyUserSettingsToBot(existingDefaultBot, user);
        if (updatedBot !== existingDefaultBot) {
          setDefaultBot(updatedBot);
          const { selectedBot: currentSelectedBot } = useBotStore.getState();
          if (!currentSelectedBot || currentSelectedBot._id === assistantId) {
            setSelectedBot(updatedBot);
          }
        } else {
          const { selectedBot: currentSelectedBot } = useBotStore.getState();
          if (!currentSelectedBot) {
            setSelectedBot(existingDefaultBot);
          }
        }
        return;
      }
      const assistantAsBot = AssistantMapper.assistantToBot(
        effectiveDefaultAssistant
      );

      const botItem: BotItem = {
        id: assistantId,
        title: effectiveDefaultAssistant.bot_name,
        description: effectiveDefaultAssistant.description,
        division: effectiveDefaultAssistant.division || '',
        workspace: effectiveDefaultAssistant.workspace || undefined,
        isFavourite: Boolean(effectiveDefaultAssistant.is_favourited),
      };

      let enhancedBot:
        | (BotItem & {
            enhancedBotDetails?: Bot;
            sessionId?: string;
            chatEndpoint?: string;
            capabilities?: Bot['capabilities'];
          })
        | null;

      try {
        enhancedBot = await ChatSessionService.enhanceBotForChat(botItem);
      } catch (_error) {
        enhancedBot = {
          ...botItem,
          enhancedBotDetails: assistantAsBot,
        };
      }

      if (isCancelled) {
        return;
      }

      const assistantConfig = AssistantMapper.assistantToChatConfig(
        effectiveDefaultAssistant
      );

      const botForStores: Bot = {
        ...assistantAsBot,
        ...(enhancedBot?.enhancedBotDetails || {}),
        k_value:
          effectiveDefaultAssistant.k_value ??
          enhancedBot?.enhancedBotDetails?.k_value ??
          assistantAsBot.k_value,
        temperature:
          effectiveDefaultAssistant.temperature ??
          enhancedBot?.enhancedBotDetails?.temperature ??
          assistantAsBot.temperature,
        system_instructions:
          effectiveDefaultAssistant.system_instructions ||
          enhancedBot?.enhancedBotDetails?.system_instructions ||
          assistantAsBot.system_instructions,
        datastore: Array.isArray(effectiveDefaultAssistant.datastore)
          ? effectiveDefaultAssistant.datastore
          : effectiveDefaultAssistant.datastore
            ? [effectiveDefaultAssistant.datastore]
            : (enhancedBot?.enhancedBotDetails?.datastore ??
              assistantAsBot.datastore),
        faqs: Array.isArray(effectiveDefaultAssistant.faqs)
          ? effectiveDefaultAssistant.faqs
          : assistantAsBot.faqs,
      };

      // Apply user settings (custom_instructions and creativity_level) to default assistant
      const botWithUserSettings = applyUserSettingsToBot(botForStores, user);

      const botWithSession = {
        ...botWithUserSettings,
        sessionId: enhancedBot?.sessionId,
        chatEndpoint: enhancedBot?.chatEndpoint,
        capabilities: enhancedBot?.capabilities,
        assistantConfig,
        is_default:
          enhancedBot?.enhancedBotDetails?.is_default ??
          assistantAsBot.is_default ??
          true,
      } as Bot & {
        sessionId?: string;
        chatEndpoint?: string;
        capabilities?: Bot['capabilities'];
        assistantConfig?: Partial<
          import('@/shared/types/api.types').ChatRequest['config']
        >;
      };

      setDefaultBot(botWithSession);

      const { selectedBot: currentSelectedBot } = useBotStore.getState();

      if (
        !currentSelectedBot ||
        currentSelectedBot._id === botWithSession._id
      ) {
        setSelectedBot(botWithSession);
      }
    };

    void initializeDefaultAssistant();

    return () => {
      isCancelled = true;
    };
  }, [effectiveDefaultAssistant, setDefaultBot, setSelectedBot, user]);

  // Create a stable reference for model names to avoid unnecessary re-runs
  const modelNames = useMemo(
    () => safeModels.map(m => m.modelName).join(','),
    [safeModels]
  );

  // Set default model using centralized utility function
  useEffect(() => {
    // Only set default model if no model is currently selected
    if (!currentModel) {
      // Try to select the default model
      const defaultModel = selectDefaultModel(
        safeModels,
        preferences?.default_model
      );
      if (defaultModel) {
        setCurrentModel(defaultModel);
      } else if (
        preferences?.default_model &&
        'modelName' in preferences.default_model
      ) {
        // If we have user preferences but no models loaded yet, set the user's default
        setCurrentModel(preferences.default_model as Model);
      } else if (safeModels.length > 0) {
        // Fallback to first available model only if we have models and no user preference
        setCurrentModel(safeModels[0]);
      }
    } else {
      // Check if we're in a multimodal conversation with a locked model
      // In this case, the model is locked and should not be changed
      if (hasMultimodalFilesInConversation) {
        return;
      }

      // Check if we're in assistant chat with a model preset
      const isAssistantChatWithModelPreset =
        chatSource === 'assistant' &&
        currentBot?.default_model_detail &&
        currentModel.modelName === currentBot.default_model_detail.modelName;

      if (isAssistantChatWithModelPreset) {
        return;
      }

      // Check if current model needs updating based on user preferences
      if (
        preferences?.default_model &&
        currentModel.modelName !== preferences.default_model.modelName
      ) {
        const defaultModel = selectDefaultModel(
          safeModels,
          preferences.default_model
        );
        if (defaultModel) {
          setCurrentModel(defaultModel);
        }
      }
    }
  }, [
    preferences?.default_model,
    modelNames,
    safeModels,
    currentModel,
    setCurrentModel,
    preferences,
    authUser,
    chatSource,
    currentBot,
    hasMultimodalFilesInConversation,
  ]);

  return <>{children}</>;
}
